// Libs
import { ChangeEvent, memo, useEffect, useState } from 'react';
import { Autocomplete, useLoadScript } from '@react-google-maps/api';
// Components, Layouts, Pages
import { BaseInput } from '~/components/common';
// Others
import { IBaseAddress } from '~/utils/interface/common';
import { PlaceAddressEnum } from '~/utils/enum/common';
import { googleMapApiKey } from '~/utils/constants/env';
import { DEFAULT_GG_MAP_LOAD_SCRIPT_LIB, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import './BaseInputAddress.scss';

type Props = {
  label: string;
  value?: string;
  errorMessage?: string;
  required?: boolean;
  onChange?: (value: string) => void;
  onSelectAddress?: (value: IBaseAddress) => void;
};

const BaseInputAddress = (props: Props) => {
  //#region Destructuring Props
  const { label, value, errorMessage, required, onChange, onSelectAddress } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapApiKey ?? EMPTY_STRING,
    libraries: DEFAULT_GG_MAP_LOAD_SCRIPT_LIB,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [autoCompleteAddress, setAutoCompleteAddress] = useState<google.maps.places.Autocomplete>();
  const [inputValue, setInputValue] = useState<string>(value || EMPTY_STRING);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    setInputValue(value || EMPTY_STRING);
  }, [value]);
  //#endregion Implement Hook

  //#region Handle Function
  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    setAutoCompleteAddress(autocomplete);
  };

  const onPlaceChanged = () => {
    if (!autoCompleteAddress) return;

    const place = autoCompleteAddress?.getPlace();

    const addressComponents = place.address_components || [];

    const getAddressComponent = (type: string) => {
      const value = addressComponents.find((address) => address.types.includes(type));
      if (!value) return EMPTY_STRING;

      return type === PlaceAddressEnum.COUNTRY ? value.long_name : value.short_name;
    };

    const fullAddress = place.formatted_address || EMPTY_STRING;
    const city = getAddressComponent(PlaceAddressEnum.LOCALITY);
    const state = getAddressComponent(PlaceAddressEnum.STATE);
    const zipCode = getAddressComponent(PlaceAddressEnum.ZIP_CODE);
    const country = getAddressComponent(PlaceAddressEnum.COUNTRY);

    const location = place.geometry?.location;
    const latitude = location?.lat();
    const longitude = location?.lng();

    const updatedAddress: IBaseAddress = {
      address: fullAddress,
      city: city,
      state: state,
      country: country,
      zipCode: zipCode,
      latitude: latitude,
      longitude: longitude,
    };

    setInputValue(updatedAddress.address as string);
    onSelectAddress && onSelectAddress(updatedAddress);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    setInputValue(newValue);
    onChange && onChange(newValue);
  };
  //#endregion Handle Function

  return (
    isLoaded && (
      <Autocomplete onPlaceChanged={onPlaceChanged} onLoad={onLoad}>
        <BaseInput
          required={required}
          label={label}
          value={inputValue}
          onChange={handleInputChange}
          errorMessage={errorMessage}
        />
      </Autocomplete>
    )
  );
};

export default memo(BaseInputAddress);
